import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Inbox, Mail, Phone, Trash2, Reply, Loader2 } from "lucide-react";

const STATUS = {
  new: { label: "New", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  read: { label: "Read", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  contacted: { label: "Contacted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  archived: { label: "Archived", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const TABS = [["all", "All"], ["new", "New"], ["contacted", "Contacted"], ["archived", "Archived"]];

export default function Enquiries() {
  const { companyId } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = () => {
    setLoading(true);
    api.get(`/companies/${companyId}/enquiries`)
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Failed to load enquiries"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const setStatus = async (e, status) => {
    setItems((arr) => arr.map((x) => (x.id === e.id ? { ...x, status } : x)));
    try { await api.put(`/companies/${companyId}/enquiries/${e.id}`, { status }); }
    catch { toast.error("Update failed"); load(); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/companies/${companyId}/enquiries/${id}`);
    toast.success("Deleted"); load();
  };

  const filtered = tab === "all" ? items : items.filter((x) => (x.status || "new") === tab);
  const newCount = items.filter((x) => (x.status || "new") === "new").length;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="enquiries-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Enquiries</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          {items.length} total{newCount > 0 && <span className="text-indigo-600 font-semibold"> · {newCount} new</span>} — leads from your public profile
        </p>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`enquiry-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === k ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {label}{k === "new" && newCount > 0 ? ` (${newCount})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-indigo-600" size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400 flex flex-col items-center gap-3">
          <Inbox size={30} /> No enquiries here yet.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((e) => {
            const st = STATUS[e.status || "new"] || STATUS.new;
            return (
              <div key={e.id} data-testid={`enquiry-${e.id}`}
                className="bg-white rounded-2xl border border-slate-200 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-slate-800">{e.name}</h3>
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 flex-wrap">
                      <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-indigo-600"><Mail size={14} /> {e.email}</a>
                      {e.phone && <span className="flex items-center gap-1.5"><Phone size={14} /> {e.phone}</span>}
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(e.created_at)}</span>
                </div>

                {e.message && <p className="text-sm text-slate-600 mt-3 bg-slate-50 rounded-xl p-3 leading-relaxed">{e.message}</p>}

                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  <select data-testid={`enquiry-status-${e.id}`} value={e.status || "new"} onChange={(ev) => setStatus(e, ev.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm bg-white outline-none">
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="contacted">Contacted</option>
                    <option value="archived">Archived</option>
                  </select>
                  <a href={`mailto:${e.email}?subject=Re: Your enquiry`} data-testid={`enquiry-reply-${e.id}`}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
                    <Reply size={14} /> Reply
                  </a>
                  <button onClick={() => del(e.id)} data-testid={`enquiry-delete-${e.id}`}
                    className="px-3 py-1.5 rounded-lg text-sm font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-1.5 ml-auto">
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
